import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { AppState } from '@state';
import { CreateMessageInMyChat, SupportChat, UploadMessageAttachmentsOpts } from '@interfaces';
import * as chatActions from './chat.actions';
import * as chatSelectors from './chat.selectors';
import { defer, filter, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable()
export class ChatFacade {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly actions = inject(Actions);

  public myChat$ = this.store.select(chatSelectors.selectChat);
  public chatMessages$ = this.store.select(chatSelectors.selectChatMessages);
  public attachmentsToAdd$ = this.store.select(chatSelectors.selectAttachmentsToAdd);

  public loading$ = this.store.select(chatSelectors.selectLoading);
  public loadingAttachments$ = this.store.select(chatSelectors.selectLoadingAttachments);
  public errorMessage$ = this.store.select(chatSelectors.selectErrorMessage);

  public addChatMessageSuccess$ = this.actions.pipe(ofType(chatActions.addChatMessageSuccess));
  public getMyChatSuccess$ = this.actions.pipe(ofType(chatActions.getMyChatSuccess));

  public getMyChat(): void {
    this.store.dispatch(chatActions.getMyChat());
  }

  public getMyChat$(): Observable<SupportChat> {
    return defer(() => {
      let chatRequested = false;

      return this.store.select(chatSelectors.selectChat).pipe(
        tap(chat => {
          if (!chat && !chatRequested) {
            chatRequested = true;
            this.getMyChat();
          }
        }),
        filter(chatState => {
          if (!chatState) {
            return false;
          }
          return true;
        })
      );
    });
  }

  public addChatMessage(opts: CreateMessageInMyChat): void {
    this.store.dispatch(chatActions.addChatMessage({ opts }));
  }

  public uploadMessageAttachments(opts: UploadMessageAttachmentsOpts): void {
    this.store.dispatch(chatActions.uploadAttachments({ opts }));
  }

  public downloadAttachmentFromChat(messageId: string, attachmentId: string, previewTargetKey?: string): void {
    this.store.dispatch(chatActions.downloadAttachmentFromChat({ messageId, attachmentId, previewTargetKey }));
  }

  public removeAttachmentFromUploading(opts: { userAssetId: string; fileOriginalName: string }): void {
    this.store.dispatch(chatActions.removeAttachmentFromUploading({ opts }));
  }
}
